import React, { useCallback, useEffect, useRef, useState } from 'react';
import './MobileAiChatCard.css';
import { useAuth } from '../../contexts/AuthContext';
import { useAiChat } from '../../hooks/useAiChat';
import RichMarkdown from '../../components/common/RichMarkdown';
import MobileDocumentSelectionModal from '../MobileDocumentSelectionModal';

export default function MobileAiChatCard({
  mode = 'standard',          // 'standard' | 'endeavor'
  endeavorId = null,
  endeavorName = ''
}) {
  const { user } = useAuth();
  const {
    isAiServiceAvailable,
    processUserMessageStream,
    loadExistingConversations,
    loadConversationMessages,
    createNewConversation,
    getQualityLevelOptions,
    loadConversationDetails
  } = useAiChat(endeavorId || 'all');

  // Conversation state (single conversation)
  const [conversation, setConversation] = useState([]);
  const [messages, setMessages] = useState([]);

  // UI state
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [qualityLevel, setQualityLevel] = useState('balanced');
  const [showQuality, setShowQuality] = useState(false);

  // Docs selection (hidden/locked in endeavor mode)
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showDocs, setShowDocs] = useState(false);

  const messagesRef = useRef(null);
  const activeStreamRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    const snap = () => { el.scrollTop = el.scrollHeight; };
    requestAnimationFrame(() => {
      snap();
      setTimeout(snap, 40);
    });
  }, []);

  // On mount and whenever messages change
  useEffect(() => { const t = setTimeout(scrollToBottom, 0); return () => clearTimeout(t); }, [scrollToBottom]);
  useEffect(() => { if (messages.length) scrollToBottom(); }, [messages, scrollToBottom]);

  // Cleanup active stream on unmount
  useEffect(() => {
    return () => {
      try { 
        activeStreamRef.current?.cancel?.(); 
      } catch {}
    };
  }, []);

  // Ensure a single conversation
  useEffect(() => {
    const init = async () => {
      if (!user || conversation) return;
      const uid = user.id || user.userId;

     // In endeavor mode, use the same conversation title as desktop: "Course: {name}"
     if (mode === 'endeavor' && endeavorName) {
       const list = await loadExistingConversations(uid);
       const desired = `Course: ${endeavorName}`;
       const match = list.find(c => c.title === desired || c.title === endeavorName);
       if (match) {
         const res = await loadConversationMessages(match.id || match.conversationId, { page: 0, size: 10 });
         setConversation({ ...match, conversationId: match.id || match.conversationId });
         setMessages(res.messages || []);
         return;
       }
       const created = await createNewConversation(desired);
       if (created) {
         setConversation({ ...created, conversationId: created.id, title: desired });
         setMessages([]);
       }
       return;
     }

     // Standard mobile chat (single shared conversation)
     const list = await loadExistingConversations(uid);
     const match = list.find(c =>
       /^mobile chat/i.test(c.title) || /^ai chat/i.test(c.title) || /^chat$/i.test(c.title)
     );
     if (match) {
       const res = await loadConversationMessages(match.id || match.conversationId, { page: 0, size: 10 });
       setConversation({ ...match, conversationId: match.id || match.conversationId });
       setMessages(res.messages || []);
     } else {
       const created = await createNewConversation('Mobile Chat');
       if (created) {
         setConversation({ ...created, conversationId: created.id });
         setMessages([]);
       }
     }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, mode, endeavorName, loadExistingConversations, loadConversationMessages, createNewConversation]);

  // After setting/receiving a conversation, hydrate persisted attachments
  useEffect(() => {
    if (!conversation?.conversationId) return;
    let ignore = false;
    (async () => {
      const details = await loadConversationDetails(conversation.conversationId);
      if (ignore || !details) return;
      if (Array.isArray(details.attachedDocumentIds)) {
        setSelectedDocuments(details.attachedDocumentIds.map(String));
      }
    })();
    return () => { ignore = true; };
  }, [conversation, loadConversationDetails]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading || !isAiServiceAvailable) return;

    // Ensure we have a conversation
    let conv = conversation;
    if (!conv) {
     const created = await createNewConversation(
       mode === 'endeavor' && endeavorName ? `Course: ${endeavorName}` : 'Mobile Chat'
     );
      if (!created) return;
      conv = { ...created, conversationId: created.id };
      setConversation(conv);
    }

    const text = input.trim();
    const userMsg = { id: `u_${Date.now()}`, sender: 'user', text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    // Create AI message with thinking state
    const aiId = `ai_${Date.now()}`;
    const aiMsg = { 
      id: aiId, 
      sender: 'ai', 
      isThinking: true, 
      isStreaming: false,
      text: '', 
      timestamp: new Date(),
      documentReferences: [],
      suggestedActions: []
    };
    setMessages(prev => [...prev, aiMsg]);

    // Determine which documents to use
    const docIdsForRequest = (mode === 'endeavor' && endeavorId) 
      ? [] 
      : selectedDocuments;

    try {
      // Cancel any prior stream
      try { activeStreamRef.current?.cancel?.(); } catch {}
      
      activeStreamRef.current = await processUserMessageStream(
        text,
        {
          conversationId: conv.conversationId || conv.id,
          ...(mode === 'endeavor' && endeavorId
            ? { endeavorId }
            : { selectedDocuments: docIdsForRequest }),
          quality_level: qualityLevel
        },
        {
          onMeta: (meta) => {
            setMessages(prev => prev.map(m => m.id === aiId ? ({
              ...m,
              documentReferences: meta?.chunks_metadata || meta?.sources || m.documentReferences,
              qualityLevel: meta?.quality_level || m.qualityLevel
            }) : m));
          },
          onToken: (chunk) => {
            if (!chunk) return;
            setMessages(prev => prev.map(m => m.id === aiId
              ? ({
                  ...m,
                  text: (m.text || '') + chunk,
                  isThinking: false,
                  isStreaming: true
                })
              : m
            ));
            // Auto-scroll to show new content
            requestAnimationFrame(scrollToBottom);
          },
          onDone: (payload) => {
            setMessages(prev => prev.map(m => m.id === aiId ? ({
              ...m,
              isThinking: false,
              isStreaming: false,
              text: payload?.response || m.text,
              tokenCount: payload?.tokens_used || m.tokenCount,
              suggestedActions: payload?.suggested_actions || m.suggestedActions
            }) : m));
            activeStreamRef.current = null;
            setIsLoading(false);
          },
          onError: (err) => {
            setMessages(prev => prev.map(m => m.id === aiId ? ({
              ...m,
              isThinking: false,
              isStreaming: false,
              text: m.text || (typeof err === 'string' ? err : 'There was a problem generating a response.')
            }) : m));
            activeStreamRef.current = null;
            setIsLoading(false);
          }
        }
      );
    } catch {
      setMessages(prev => prev.map(m => m.id === aiId ? ({
        ...m,
        isThinking: false,
        isStreaming: false,
        text: 'Error contacting AI service. Please retry.'
      }) : m));
      setIsLoading(false);
    }
  };

  return (
    <div className="m-ai-card">
      <div className="m-ai-topbar">
        {mode !== 'endeavor' && (
          <button
            type="button"
            className="m-docs-btn"
            onClick={() => setShowDocs(true)}
            title="Select documents"
          >
            📄 Docs{selectedDocuments.length ? ` (${selectedDocuments.length})` : ''}
          </button>
        )}
        <div className="m-quality">
          <button
            type="button"
            className={`m-quality-btn ${showQuality ? 'open' : ''}`}
            onClick={() => setShowQuality(o => !o)}
            aria-expanded={showQuality}
          >
            {getQualityLevelOptions().find(o => o.value === qualityLevel)?.label || 'Quality'}
            <span className="caret">{showQuality ? '▲' : '▼'}</span>
          </button>
          {showQuality && (
            <div className="m-quality-menu" role="menu">
              {getQualityLevelOptions().map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`m-quality-item ${opt.value === qualityLevel ? 'active' : ''}`}
                  onClick={() => { setQualityLevel(opt.value); setShowQuality(false); }}
                >
                  <div className="q-label">{opt.label}</div>
                  <div className="q-desc">{opt.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="m-ai-body" ref={messagesRef}>
        {(!isAiServiceAvailable) && (
          <div className="m-ai-status">Spinning up AI service…</div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`m-row ${m.sender}`}>
            <div className={`m-bubble ${m.sender}`}>
              {(m.isThinking && !m.text) ? (
                <div className="m-typing"><span /><span /><span /></div>
              ) : (
                <>
                  <div className="m-text">
                    <RichMarkdown text={m.text || ''} />
                  </div>
                  {m.documentReferences?.length > 0 && (
                    <div className="m-refs">📄 {Math.min(3, m.documentReferences.length)} ref{m.documentReferences.length > 1 ? 's' : ''}</div>
                  )}
                </>
              )}
              <div className="m-stamp">
                {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <form className="m-ai-input" onSubmit={sendMessage}>
        <input
          placeholder="Ask a question…"
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={isLoading || !isAiServiceAvailable}
        />
        <button
          type="submit"
          disabled={!input.trim() || isLoading || !isAiServiceAvailable}
        >
          {isLoading ? '…' : 'Send'}
        </button>
      </form>

      {mode !== 'endeavor' && showDocs && (
        <MobileDocumentSelectionModal
          isOpen={showDocs}
          onClose={() => setShowDocs(false)}
          selectedDocuments={selectedDocuments}
          onToggleDocument={(id) => {
            const s = String(id);
            setSelectedDocuments(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
          }}
          onConnectDocuments={(arr) => { setSelectedDocuments(arr.map(String)); setShowDocs(false); }}
          requiredStatusIds={[7]}
          title="Select Documents for Context"
          description="Choose processed documents to ground the AI responses."
        />
      )}
    </div>
  );
}